// Forecast Workbench: pick a hub + date and see exactly how each customer's
// forecast is built — base × growth × allocation% × day-of-week × seasonal — and
// override any value with a reason. Overrides flow into every forecast view.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Card, Field, Input, Select } from "../../components/ui";
import { getErrorMessage } from "../../lib/errors";
import { fmtN, today } from "../../lib/format";
import { parseOptionalNumber } from "../../lib/validation";
import type { Customer, Hub } from "../../lib/types";
import { explainCustomerHub, type ForecastBreakdown } from "../../lib/forecast/engine";
import { listCustomers } from "../customers/customersApi";
import { listHubs } from "../hubs/hubsApi";
import {
  deleteOverride,
  listAccountOverrides,
  saveOverride,
  type ForecastOverride,
} from "./overridesApi";

interface Row {
  customer: Customer;
  breakdown: ForecastBreakdown;
}

export default function ForecastWorkbench() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [overrides, setOverrides] = useState<ForecastOverride[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hubId, setHubId] = useState("");
  const [date, setDate] = useState(today());
  const [valueInput, setValueInput] = useState<Record<string, string>>({});
  const [reasonInput, setReasonInput] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  const loadMeta = useCallback(async () => {
    if (!accountId) return;
    try {
      const [c, h, o] = await Promise.all([
        listCustomers(accountId),
        listHubs(accountId),
        listAccountOverrides(accountId),
      ]);
      setCustomers(c);
      setHubs(h.filter((x) => x.active));
      setOverrides(o);
      setHubId((prev) => prev || h.filter((x) => x.active)[0]?.id || "");
    } catch (e) {
      setError(getErrorMessage(e, "Failed to load workbench data."));
    }
  }, [accountId]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  // Overrides for the current hub+date, keyed by customer.
  const cellOverrides = useMemo(() => {
    const m = new Map<string, ForecastOverride>();
    for (const o of overrides) if (o.hubId === hubId && o.date === date) m.set(o.customerId, o);
    return m;
  }, [overrides, hubId, date]);

  // Rows: customers eligible at this hub/date, plus any with an override.
  const rows = useMemo<Row[]>(() => {
    if (!hubId) return [];
    return customers
      .map((c) => ({ customer: c, breakdown: explainCustomerHub(c, hubId, date) }))
      .filter((r) => r.breakdown.eligible || cellOverrides.has(r.customer.id))
      .sort((a, b) => b.breakdown.forecast - a.breakdown.forecast);
  }, [customers, hubId, date, cellOverrides]);

  // Seed the editable inputs whenever the cell set changes.
  useEffect(() => {
    const v: Record<string, string> = {};
    const r: Record<string, string> = {};
    for (const [custId, o] of cellOverrides) {
      v[custId] = String(o.value);
      r[custId] = o.reason ?? "";
    }
    setValueInput(v);
    setReasonInput(r);
  }, [cellOverrides]);

  const effective = (row: Row) => {
    const raw = valueInput[row.customer.id];
    if (raw != null && raw.trim() !== "" && !Number.isNaN(Number(raw))) return Math.round(Number(raw));
    return row.breakdown.forecast;
  };
  const hubTotal = useMemo(() => rows.reduce((s, r) => s + effective(r), 0), [rows, valueInput]);

  const seasonalNote = rows[0]?.breakdown;

  async function save() {
    if (!accountId || !hubId) return;
    setError(null);
    try {
      for (const r of rows) {
        const id = r.customer.id;
        const raw = valueInput[id] ?? "";
        const existing = cellOverrides.get(id);
        if (raw.trim() === "") {
          if (existing) await deleteOverride(accountId, id, hubId, date);
          continue;
        }
        const n = parseOptionalNumber(raw);
        if (!n.ok || n.value === null || n.value < 0) {
          setError(`${r.customer.name}: override must be a positive number.`);
          return;
        }
        await saveOverride(accountId, {
          customerId: id,
          hubId,
          date,
          value: Math.round(n.value),
          reason: reasonInput[id],
        });
      }
      setSaving(true);
      setSavedMsg("Overrides saved.");
      const o = await listAccountOverrides(accountId);
      setOverrides(o);
    } catch (e) {
      setError(getErrorMessage(e, "Failed to save overrides."));
    } finally {
      setSaving(false);
    }
  }

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold tracking-tight text-slate-900">Forecast Workbench</h1>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card>
        <div className="grid max-w-xl grid-cols-2 gap-3">
          <Field label="Hub">
            <Select value={hubId} onChange={(e) => setHubId(e.target.value)}>
              {hubs.length === 0 && <option value="">No active hubs</option>}
              {hubs.map((h) => (
                <option key={h.id} value={h.id}>
                  {h.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Date">
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </Field>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Forecast = Daily base × Growth × Allocation% × Day-of-week × Seasonal.
          {seasonalNote && (
            <>
              {" "}
              For this date: DOW ×{seasonalNote.dowFactor.toFixed(2)}, Seasonal ×
              {seasonalNote.seasonalFactor.toFixed(2)}
              {seasonalNote.isHoliday && (
                <span className="font-medium text-amber-700"> (holiday)</span>
              )}
              .
            </>
          )}
        </p>
      </Card>

      {hubId && (
        <Card className="p-0">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
            <span className="text-sm font-medium text-slate-700">
              Hub total: <span className="font-semibold">{fmtN(hubTotal)}</span> pkg
            </span>
            <div className="flex items-center gap-3">
              {savedMsg && <span className="text-sm text-emerald-600">{savedMsg}</span>}
              <Button onClick={save} disabled={saving}>
                {saving ? "Saving…" : "Save overrides"}
              </Button>
            </div>
          </div>
          {rows.length === 0 ? (
            <p className="p-5 text-sm text-slate-500">No customers forecast at this hub on this date.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2 font-medium">Customer</th>
                  <th className="px-3 py-2 text-right font-medium">Base</th>
                  <th className="px-3 py-2 text-right font-medium">×Growth</th>
                  <th className="px-3 py-2 text-right font-medium">Alloc%</th>
                  <th className="px-3 py-2 text-right font-medium">×DOW</th>
                  <th className="px-3 py-2 text-right font-medium">×Seas</th>
                  <th className="px-3 py-2 text-right font-medium">Computed</th>
                  <th className="px-3 py-2 font-medium">Override</th>
                  <th className="px-4 py-2 font-medium">Reason</th>
                  <th className="px-4 py-2 text-right font-medium">Effective</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const b = r.breakdown;
                  const id = r.customer.id;
                  const overridden = (valueInput[id] ?? "").trim() !== "";
                  return (
                    <tr key={id} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-1.5 font-medium text-slate-900">
                        {r.customer.name}
                        {!b.eligible && (
                          <span className="ml-1 text-xs text-amber-700">({b.reason})</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right text-slate-600">{fmtN(b.base)}</td>
                      <td className="px-3 py-1.5 text-right text-slate-600">{b.growthFactor.toFixed(3)}</td>
                      <td className="px-3 py-1.5 text-right text-slate-600">{b.allocationPct}%</td>
                      <td className="px-3 py-1.5 text-right text-slate-600">{b.dowFactor.toFixed(2)}</td>
                      <td className="px-3 py-1.5 text-right text-slate-600">{b.seasonalFactor.toFixed(2)}</td>
                      <td className="px-3 py-1.5 text-right text-slate-700">{fmtN(b.forecast)}</td>
                      <td className="w-24 px-3 py-1">
                        <Input
                          type="number"
                          min={0}
                          value={valueInput[id] ?? ""}
                          onChange={(e) => {
                            setValueInput((p) => ({ ...p, [id]: e.target.value }));
                            setSavedMsg(null);
                          }}
                          placeholder="—"
                        />
                      </td>
                      <td className="w-44 px-4 py-1">
                        <Input
                          value={reasonInput[id] ?? ""}
                          onChange={(e) => setReasonInput((p) => ({ ...p, [id]: e.target.value }))}
                          placeholder="optional"
                        />
                      </td>
                      <td
                        className={`px-4 py-1.5 text-right font-medium ${overridden ? "text-brand-700" : "text-slate-900"}`}
                      >
                        {fmtN(effective(r))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </div>
  );
}
